<template>
  <div class="page vld-parent" v-if="environment">
    <loading :active="isLoading" :is-full-page="false" loader="bars" />

    <div class="page-main">
      <div class="page-header">
        <div class="col-md-12 col-lg-12 col-xl-12 col-xxl-10 offset-xxl-1">
          <h1 class="page-title">Samurai</h1>
          <breadcrumb
            name="GAME_SAMURAI_SETTINGS_DETAIL"
            :withLast.sync="environment.name"
          ></breadcrumb>

          <div class="page-title page-header-actions">
            <span class="save-badge" :class="saveStateClass">
              {{ saveStateText }}
            </span>

            <button
              type="button"
              class="btn btn-round btn-default"
              @click="goBack"
            >
              <i
                class="icon mdi mdi-chevron-left mdi-18px"
                aria-hidden="true"
              ></i>
              Back
            </button>
          </div>
        </div>
      </div>

      <div class="page-content container-fluid">
        <div class="row">
          <div class="col-md-12 col-lg-12 col-xl-12 col-xxl-10 offset-xxl-1">
            <div class="panel panel-bordered mb-30">
              <div class="panel-heading">
                <h3 class="panel-title">
                  Ufu Settings - {{ environment.name }}
                </h3>
                <div class="panel-actions panel-actions-keep"></div>
              </div>
              <div class="panel-body">
                <EnvironmentConfigForm
                  :value="form"
                  :themes="themes"
                  :save-state="saveState"
                  @change="onFormChange"
                  @blur="onFormBlur"
                />
              </div>
            </div>

            <QuestionList :code="environment.code" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

import { gameSamuraiService } from "@/api/client";
import EnvironmentConfigForm from "@/views/samurai/Settings/components/EnvironmentConfigForm.vue";
import QuestionList from "@/views/samurai/Settings/components/QuestionList.vue";

const defaultForm = () => ({
  play_sec: 0,
  point_0: 0,
  point_1: 0,
  point_2: 0,
  point_bomb: 0,
  point_vip: 0,
  theme_id: 0,
  play_day_fri: 0,
  play_day_mon: 0,
  play_day_sat: 0,
  play_day_sun: 0,
  play_day_thu: 0,
  play_day_tue: 0,
  play_day_wed: 0,
});

const normalizeForm = (config = {}) => {
  const form = defaultForm();

  Object.keys(form).forEach((key) => {
    if (config[key] !== undefined && config[key] !== null) {
      form[key] = config[key];
    }
  });

  return form;
};

export default {
  name: "GameSamuraiSettingsDetail",

  components: {
    Loading,
    EnvironmentConfigForm,
    QuestionList,
  },

  data() {
    return {
      environment: null,
      form: defaultForm(),
      themes: [],
      isLoading: false,
      saveState: "saved",
      isInitializing: true,
      currentSavePromise: null,
    };
  },

  computed: {
    code() {
      return this.$route.params.code;
    },

    saveStateClass() {
      return {
        "is-saved": this.saveState === "saved",
        "is-saving": this.saveState === "saving",
        "is-unsaved": this.saveState === "unsaved",
      };
    },

    saveStateText() {
      switch (this.saveState) {
        case "saving":
          return "Saving...";
        case "unsaved":
          return "Unsaved";
        default:
          return "Saved";
      }
    },
  },

  async mounted() {
    await this.fetchPageData();
  },

  beforeRouteLeave(to, from, next) {
    this.flushPendingSave()
      .then(() => next())
      .catch(() => next());
  },

  methods: {
    isUfuCode(code) {
      return /^ufu\d+$/i.test(code || "");
    },

    async fetchPageData() {
      if (!this.isUfuCode(this.code)) {
        this.$router.replace({ name: "GameSamuraiSettings" }).catch(() => {});
        return;
      }

      this.isLoading = true;
      this.isInitializing = true;

      try {
        const [groups, config, themes] = await Promise.all([
          gameSamuraiService.getUfuGroups(),
          gameSamuraiService.getByCode(this.code),
          gameSamuraiService.getThemeChoice(),
        ]);

        const environment = groups.find(
          (group) => group.code === this.code
        ) || {
          name: (this.code || "").toUpperCase(),
          code: this.code,
        };

        this.environment = environment;
        this.themes = themes;
        this.form = normalizeForm(config);
        this.saveState = "saved";
      } catch (error) {
        error.response
          ? this.$dialogs.alert(error.response.data)
          : this.$dialogs.alert(error.message || error);
      } finally {
        this.isLoading = false;
        this.$nextTick(() => {
          this.isInitializing = false;
        });
      }
    },

    onFormChange(nextForm) {
      this.form = Object.assign({}, this.form, nextForm);

      if (this.isInitializing) return;

      this.saveState = "unsaved";
    },

    async onFormBlur() {
      if (this.isInitializing || this.saveState !== "unsaved") return;
      await this.persistConfig();
    },

    async persistConfig() {
      if (!this.environment) return;
      if (this.currentSavePromise) {
        await this.currentSavePromise;
        return;
      }

      this.saveState = "saving";

      try {
        this.currentSavePromise = gameSamuraiService.updateConfig(
          this.environment.code,
          normalizeForm(this.form)
        );
        await this.currentSavePromise;
        this.saveState = "saved";
      } catch (error) {
        this.saveState = "unsaved";
        error.response
          ? this.$dialogs.alert(error.response.data)
          : this.$dialogs.alert(error.message || error);
      } finally {
        this.currentSavePromise = null;
      }
    },

    async flushPendingSave() {
      if (this.saveState === "saving" && this.currentSavePromise) {
        await this.currentSavePromise;
        return;
      }

      if (this.saveState !== "unsaved") return;

      await this.persistConfig();
    },

    async goBack() {
      await this.flushPendingSave();
      this.$router.push({ name: "GameSamuraiSettings" }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
.page-header-actions {
  right: 0px;
  gap: 1rem;
  display: flex;
  align-items: center;

  button {
    gap: 0.5rem;
    display: flex;
    align-items: center;

    i {
      margin-bottom: 0px;
    }
  }
}

.save-badge {
  min-width: 96px;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  text-align: center;
  font-weight: 600;
  font-size: 0.875rem;
  background: #edf1f7;
  color: #526069;

  &.is-saved {
    color: #0b6b2b;
    background: #e7f7ed;
  }

  &.is-saving {
    color: #8a5a00;
    background: #fff4d8;
  }

  &.is-unsaved {
    color: #a94442;
    background: #fde8e7;
  }
}

.vld-overlay.is-active {
  height: 100vh;
  position: fixed;
}
</style>
